import state from '@state';
import { processQuery } from '@query/processor';
import { log } from '@utils/logger';
import { AexResponse } from '@components/AexResponse';
import { Instance } from 'types';
import { v4 as uuidv4 } from 'uuid';
import i18n from '@i18n';

const boundForms = new WeakSet<HTMLFormElement>();
const formInstanceMap = new WeakMap<HTMLFormElement, Instance>();

/**
 * Overrides the form's submit event to send queries through AgentEx.
 * Handles basic validation and error feedback.
 *
 * @param form - The HTML form element to override.
 * @param instance - The configuration instance for this form.
 */
export async function setupSubmitHandler(form: HTMLFormElement, instance: Instance): Promise<void> {
	if (!form || boundForms.has(form)) {
		return;
	}

	formInstanceMap.set(form, instance);
	boundForms.add(form);

	// Ensure one delegated listener is added
	if (!(document.body as any).__aexDelegatedSubmitBound) {
		(document.body as any).__aexDelegatedSubmitBound = true;

		document.body.addEventListener('submit', async (event) => {
			const target = event.target as HTMLFormElement;
			if (!target || !formInstanceMap.has(target)) {
				return;
			}

			const instance = formInstanceMap.get(target)!;

			const searchInput = target.querySelector(instance.searchField || 'input[type="search"]') as HTMLInputElement;

			if (!searchInput) {
				log(i18n.t('log.noSearchInput'), [target]);
				return;
			}
			event.preventDefault();
			event.stopImmediatePropagation();

			const query = searchInput.value.trim();

			// Clear existing responses
			const existingResponse = document.querySelector(`.aex-response-${instance.index}`) as HTMLElement | null;
			if (existingResponse) {
				const parent = existingResponse.parentNode;
				if (parent instanceof Element && parent.classList.contains('aex-response-wrapper')) {
					parent.remove();
				} else {
					existingResponse.remove();
				}

				searchInput.removeAttribute('aria-invalid');
				searchInput.removeAttribute('aria-describedby');
			}

			if (!query) {
				const errorId = `aex-search-error-${uuidv4()}`;

				await AexResponse({
					target: instance.responseContainer,
					position: instance.responsePosition,
					type: 'error',
					responseWrapperClass: instance.responseWrapperClass,
					containerClass: instance.responseClass,
					responseButtonClass: instance.responseButtonClass,
					index: instance.index || '1',
					id: errorId,
					content: {
						errorText: i18n.t('error.emptyInput'),
					},
					searchFallback: false,
					feedback: false,
					rtl: false,
				});

				searchInput.setAttribute('aria-invalid', 'true');
				searchInput.setAttribute('aria-describedby', errorId);

				document.querySelector(`#${errorId} .aex-response__close-button`)?.addEventListener('click', () => {
					searchInput.removeAttribute('aria-invalid');
					searchInput.removeAttribute('aria-describedby');
					const error = document.querySelector(`#${errorId}`);
					const parent = error?.parentNode;
					if (parent instanceof Element && parent.classList.contains('aex-response-wrapper')) {
						parent.remove();
					} else {
						error?.remove();
					}

					searchInput.focus();
				});

				return;
			}

			state.currentQuery = query;
			state.currentInstance = instance;
			log(i18n.t('log.submitting'), [query]);
			await processQuery(query);
		}, true);
	}
}
